<script setup>
import DotsThreeVerticalIcon from '@iconify-vue/ph/dots-three-vertical';
import Gallery from '@js/components/files/Gallery.vue';
import { Motion } from 'motion-v';
import {
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuRoot,
    DropdownMenuTrigger,
} from 'reka-ui';
import { computed, ref } from 'vue';

const {
    name,
    message,
    date,
    attachments = [],
    right = false,
    isScheduled = null,
    enableLineBreaks = false,
    isWysiwygHtml = false,
} = defineProps({
    name: {
        type: String,
        required: true,
    },
    message: {
        type: String,
        required: true,
    },
    date: {
        type: String,
        required: true,
    },
    attachments: {
        type: Array,
        required: false,
    },
    right: {
        type: Boolean,
        required: false,
    },
    isScheduled: {
        type: Boolean,
        required: false,
    },
    enableLineBreaks: {
        type: Boolean,
        required: false,
    },
    isWysiwygHtml: {
        type: Boolean,
        required: false,
    },
});

const formattedMessage = computed(() => {
    let tempMessage = message;

    if (!enableLineBreaks) {
        tempMessage = tempMessage.replace(/(\r?\n){2,}/g, '\n');
    }

    return tempMessage.replace(
        /(https?:\/\/[^\s]+)/g,
        (url) =>
            `<a href="${url}" class="text-indigo-600 underline" target="_blank">${url}</a>`,
    );
});

const formattedName = computed(() => {
    return name.charAt(0).toUpperCase();
});

const isMenuOpen = ref(false);
</script>

<template>
    <div class="flex items-end gap-2" :class="{ 'flex-row-reverse': right }">
        <div
            class="inline-flex size-10 items-center justify-center rounded-full font-bold uppercase"
            :class="{
                'bg-gray-800 text-white': !right,
                'border border-gray-200 bg-white text-gray-800': right,
            }"
        >
            {{ formattedName }}
        </div>

        <div
            class="max-w-[80%] rounded-t-lg px-4 py-3"
            :class="{
                'rounded-br-lg border border-gray-200 bg-white': !right,
                'rounded-bl-lg bg-gray-200': right,
            }"
        >
            <div class="mb-4 flex items-center justify-between gap-4">
                <div class="name font-bold">
                    {{ name }}
                </div>

                <div class="ml-auto text-xs italic">
                    <template v-if="isScheduled">
                        Planifié le&nbsp;:&nbsp;
                    </template>

                    {{ date }}
                </div>

                <DropdownMenuRoot
                    v-if="$slots['edit-action'] || $slots['delete-action']"
                    v-model:open="isMenuOpen"
                    :modal="false"
                >
                    <DropdownMenuTrigger
                        class="group flex items-center justify-center outline-none"
                    >
                        <DotsThreeVerticalIcon
                            class="size-5 text-gray-500 duration-200 group-data-[state=open]:text-gray-800 hover:text-gray-800"
                        />
                    </DropdownMenuTrigger>

                    <DropdownMenuPortal>
                        <DropdownMenuContent
                            force-mount
                            align="end"
                            :side-offset="6"
                            class="z-50 data-[state=closed]:pointer-events-none data-[state=closed]:invisible"
                        >
                            <Motion
                                :initial="{ opacity: 0, scale: 0.96, y: -4 }"
                                :animate="
                                    isMenuOpen
                                        ? { opacity: 1, scale: 1, y: 0 }
                                        : { opacity: 0, scale: 0.96, y: -4 }
                                "
                                :transition="{ duration: 0.15 }"
                                class="flex flex-col overflow-hidden rounded-sm border border-gray-300 bg-white shadow-sm"
                            >
                                <DropdownMenuItem
                                    v-if="$slots['edit-action']"
                                    class="relative inline-flex cursor-pointer items-center gap-1 px-3 py-1 text-sm whitespace-nowrap duration-150 outline-none hover:bg-indigo-600 hover:text-white data-[highlighted]:bg-indigo-600 data-[highlighted]:text-white"
                                >
                                    Modifier

                                    <slot name="edit-action" />
                                </DropdownMenuItem>

                                <DropdownMenuItem
                                    v-if="$slots['delete-action']"
                                    class="relative inline-flex cursor-pointer items-center gap-1 px-3 py-1 text-sm whitespace-nowrap duration-150 outline-none hover:bg-indigo-600 hover:text-white data-[highlighted]:bg-indigo-600 data-[highlighted]:text-white"
                                >
                                    Supprimer

                                    <slot name="delete-action" />
                                </DropdownMenuItem>
                            </Motion>
                        </DropdownMenuContent>
                    </DropdownMenuPortal>
                </DropdownMenuRoot>
            </div>

            <div
                v-if="!isWysiwygHtml"
                class="whitespace-pre-wrap"
                :class="{
                    'whitespace-pre-wrap': !enableLineBreaks,
                }"
                v-html="formattedMessage"
            ></div>

            <div v-else v-html="message" class="wysiwyg"></div>

            <div v-if="attachments && attachments.length > 0" class="mt-4">
                <Gallery :files="attachments" />
            </div>

            <div
                v-if="$slots.status"
                class="mt-3 flex"
                :class="{ 'justify-end': right }"
            >
                <slot name="status" />
            </div>
        </div>
    </div>
</template>
